<template>
	<Autocomplete
		:key="name"
		v-model="model"
		:icon="icon"
		:item-value="permission ? 'role_fk_id' : 'id'"
		:item-title="
			// Runs on both shapes: the role entity (hp_id/name) and the role pivot row
			// (fk_role_hpid/fk_role_name, no hp_id). Drop what is missing instead of
			// interpolating `undefined` into the title.
			(item: Partial<Api.Role> & Partial<Api.RoleVisibilityRole>) =>
				[item.hp_id ?? item.fk_role_hpid, item.name ?? item.fk_role_name]
					.filter(Boolean)
					.join(' - ')
		"
		:label="label"
		:multiple="multiple"
		:name="name"
		:display-name="translate({ key: 'entity.role.name', count: 2 })"
		:params="{ of: 'hp_id' }"
		endpoint="roles"
		:chips="permission || chips"
		v-bind="$attrs"
		data-test="roles-autocomplete"
	>
		<template v-if="permission" #chip="{ props, item, index, selected }">
			<v-chip v-if="index < limit || showAll" v-bind="props" label size="small"
				><span>{{
					typeof item.raw.name !== 'undefined'
						? item.raw.name
						: item.raw.fk_role_name
				}}</span>

				<v-btn
					v-tooltip:bottom="{
						text: item.raw.unlimited ? unlimited.text : startOf.text,
					}"
					class="ml-1"
					size="small"
					v-if="permission"
					@click="item.raw.unlimited = !item.raw.unlimited"
				>
					<v-icon color="primary" size="small"
						>{{ item.raw.unlimited ? unlimited.icon : startOf.icon }}
					</v-icon>
				</v-btn>
			</v-chip>
			<v-btn
				v-else-if="index === limit"
				color="primary"
				size="x-small"
				@click.stop="showAll = !showAll"
				>Alle {{ selected.length }} anzeigen...
			</v-btn>
			<v-btn
				v-if="showAll && selected.length > 10 && index === selected.length - 1"
				color="primary"
				size="x-small"
				@click.stop="showAll = !showAll"
				>Ausblenden
			</v-btn>
		</template>

		<template #item="{ item, props }">
			<v-list-item
				v-bind="props"
				:title="`${item.raw.hp_id} / ${item.raw.name}`"
			>
			</v-list-item>
		</template>

		<template v-if="$slots.append" #append>
			<slot name="append"></slot>
		</template>
	</Autocomplete>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Autocomplete from '../Validation/Autocomplete.vue'
import { useTranslate } from 'composables/useTranslate.ts'

defineProps({
	icon: {
		default: 'group',
		type: String,
	},
	label: {
		default: 'component.persons.roles.label',
		type: String,
	},
	name: {
		default: 'roles',
		type: String,
	},
	limit: {
		default: 5,
		type: Number,
	},
	permission: {
		type: Boolean,
		default: false,
	},
	multiple: {
		type: Boolean,
		default: false,
	},
	chips: {
		type: Boolean,
		default: false,
	},
	modelValue: {
		required: true,
		type: undefined,
	},
})
const { translate } = useTranslate()
const showAll = ref(false)
const startOf = ref({
	icon: 'fal fa-greater-than-equal',
	text: 'ab Beginn der PFkt',
})
const unlimited = ref({
	icon: 'fal fa-infinity',
	text: 'unbegrenzter Zugriff',
})

const model = defineModel()
</script>

<style scoped></style>
